import { FC, useState } from "react";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  FoodUsageAsIngredient,
  MenuLoopUsage,
  MenuUsage,
} from "client/jspPlatformExperiment";
import { useAppSelector } from "hooks/useReduxStore";
import userGroupOptionSelector from "store/selectors/userGroup/userGroupOptionSelector";
import Routes from "models/routes";

interface FoodUsageInfoProps {
  usedAsIngredientIn: FoodUsageAsIngredient[];
  usedInMenus: MenuUsage[];
  usedInMenuLoops: MenuLoopUsage[];
  /** Truncate each subsection to this many items with a "+N more" toggle. */
  maxItemsPerSection?: number;
  /** Header shown above the usage sections. */
  title?: string;
}

const subSectionHeaderSx = {
  fontWeight: 600,
  color: "#ccc",
  mb: 0.5,
};

const moreTogglePillSx = {
  padding: "2px 8px",
  borderRadius: 1,
  background: "rgba(0, 0, 0, 0.04)",
  cursor: "pointer",
  fontWeight: 600,
  color: "#ccc",
};

interface UsageSectionProps {
  singularLabel: string;
  pluralLabel: string;
  tooltip?: string;
  links: Array<{ key: string | number; label: string; to: string }>;
  maxItems?: number;
}

const UsageSection: FC<UsageSectionProps> = ({
  singularLabel,
  pluralLabel,
  tooltip,
  links,
  maxItems,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (links.length === 0) {
    return null;
  }
  const labelNode = (
    <Typography
      variant="body2"
      sx={{ ...subSectionHeaderSx, display: "inline-block" }}
    >
      {links.length} {links.length === 1 ? singularLabel : pluralLabel}
    </Typography>
  );
  const isTruncating = !!maxItems && !expanded && links.length > maxItems;
  const visibleLinks = isTruncating ? links.slice(0, maxItems) : links;
  const hiddenCount = links.length - visibleLinks.length;
  return (
    <Box>
      {tooltip ? <Tooltip title={tooltip}>{labelNode}</Tooltip> : labelNode}
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {visibleLinks.map((link) => (
          <Link
            key={link.key}
            component={RouterLink}
            to={link.to}
            variant="body2"
            underline="hover"
            sx={{
              padding: "2px 8px",
              borderRadius: 1,
              background: "rgba(0, 0, 0, 0.04)",
            }}
          >
            {link.label}
          </Link>
        ))}
        {hiddenCount > 0 && (
          <Tooltip title="Show all">
            <Typography
              component="span"
              variant="body2"
              onClick={() => setExpanded(true)}
              sx={moreTogglePillSx}
            >
              +{hiddenCount} more
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

interface MenuLoopUsageSectionProps {
  usages: MenuLoopUsage[];
  maxItems?: number;
}

const MenuLoopUsageSection: FC<MenuLoopUsageSectionProps> = ({
  usages,
  maxItems,
}) => {
  const siteUserGroupOptions = useAppSelector(
    userGroupOptionSelector.siteUserGroupOptions,
  );
  const siteColorMap = useAppSelector(userGroupOptionSelector.siteColorMap);
  const [expanded, setExpanded] = useState(false);
  if (usages.length === 0) {
    return null;
  }
  const isTruncating = !!maxItems && !expanded && usages.length > maxItems;
  const visibleUsages = isTruncating ? usages.slice(0, maxItems) : usages;
  const hiddenCount = usages.length - visibleUsages.length;
  return (
    <Box>
      <Tooltip title="Activated menus">
        <Typography
          variant="body2"
          sx={{ ...subSectionHeaderSx, display: "inline-block" }}
        >
          {usages.length} {usages.length === 1 ? "menu loop" : "menu loops"}
        </Typography>
      </Tooltip>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {visibleUsages.map((usage) => (
          <Stack
            key={usage.menu_loop_id}
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            gap={0.5}
            sx={{
              padding: "2px 8px",
              borderRadius: 1,
              background: "rgba(0, 0, 0, 0.04)",
            }}
          >
            <Link
              component={RouterLink}
              to={`${Routes.MENU_LOOP_EDIT}/${usage.menu_loop_id}`}
              variant="body2"
              underline="hover"
            >
              {usage.menu_loop_name}
            </Link>
            {usage.site_user_groups?.map((siteGroup) => (
              <Box
                key={siteGroup.user_group_id}
                component="span"
                sx={{
                  padding: "0 0.5rem",
                  borderRadius: "1rem",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  color: "white",
                  backgroundColor:
                    siteColorMap[siteGroup.user_group_id] ??
                    "rgb(160, 160, 160)",
                }}
              >
                {siteUserGroupOptions[siteGroup.user_group_id]?.group_name ??
                  siteGroup.group_name ??
                  `Site ${siteGroup.user_group_id}`}
              </Box>
            ))}
          </Stack>
        ))}
        {hiddenCount > 0 && (
          <Tooltip title="Show all">
            <Typography
              component="span"
              variant="body2"
              onClick={() => setExpanded(true)}
              sx={moreTogglePillSx}
            >
              +{hiddenCount} more
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

const FoodUsageInfo: FC<FoodUsageInfoProps> = ({
  usedAsIngredientIn,
  usedInMenus,
  usedInMenuLoops,
  maxItemsPerSection,
  title = "Used in",
}) => {
  if (
    usedAsIngredientIn.length === 0 &&
    usedInMenus.length === 0 &&
    usedInMenuLoops.length === 0
  ) {
    return null;
  }
  return (
    <Stack spacing={1}>
      <Typography
        sx={{
          fontSize: "1.5rem",
          fontWeight: 200,
          color: "black",
          padding: "16px 0 0 0",
        }}
      >
        {title}
      </Typography>
      <UsageSection
        singularLabel="recipe"
        pluralLabel="recipes"
        maxItems={maxItemsPerSection}
        links={usedAsIngredientIn.map((usage) => ({
          key: usage.food_id,
          label: usage.food_name,
          to: `${Routes.FOOD_RECIPE_VIEW}/${usage.food_id}`,
        }))}
      />
      <UsageSection
        singularLabel="menu"
        pluralLabel="menus"
        tooltip="Menu templates or future menus"
        maxItems={maxItemsPerSection}
        links={usedInMenus.map((usage) => ({
          key: usage.menu_id,
          label: usage.menu_name,
          to: `${Routes.MENU_EDIT}/${usage.menu_id}`,
        }))}
      />
      <MenuLoopUsageSection
        usages={usedInMenuLoops}
        maxItems={maxItemsPerSection}
      />
    </Stack>
  );
};

export default FoodUsageInfo;
